function parseQueryFilter(queryFilter: string): URLSearchParams {
    const normalized = queryFilter.startsWith("&")
        ? queryFilter.slice(1)
        : queryFilter;

    return new URLSearchParams(normalized);
}

function getNestedValue(row: Record<string, unknown>, key: string): unknown {
    if (!key.includes(".")) {
        return row[key];
    }

    return key.split(".").reduce<unknown>((current, part) => {
        if (current && typeof current === "object") {
            return (current as Record<string, unknown>)[part];
        }
        return undefined;
    }, row);
}

function matchesTextFilter(
    row: Record<string, unknown>,
    key: string,
    value: string
): boolean {
    const search = value.toLowerCase().trim();
    if (!search) {
        return true;
    }

    const candidate = getNestedValue(row, key);
    if (candidate === undefined || candidate === null) {
        return false;
    }

    if (typeof candidate === "object") {
        return JSON.stringify(candidate).toLowerCase().includes(search);
    }

    return String(candidate).toLowerCase().includes(search);
}

function matchesDateFilter(
    row: Record<string, unknown>,
    dateField: string,
    startDate?: string | null,
    endDate?: string | null
): boolean {
    if (!startDate && !endDate) {
        return true;
    }

    const rawDate = getNestedValue(row, dateField);
    if (typeof rawDate !== "string" || !rawDate) {
        return true;
    }

    const rowDate = rawDate.slice(0, 10);
    if (startDate && rowDate < startDate) {
        return false;
    }
    if (endDate && rowDate > endDate) {
        return false;
    }

    return true;
}

interface ApplyClientFiltersOptions {
    statusField?: string;
    dateField?: string;
    textFields?: string[];
}

export function applyClientFilters<T extends Record<string, unknown>>(
    rows: T[],
    queryFilter: string,
    options: ApplyClientFiltersOptions = {}
): T[] {
    if (!queryFilter) {
        return rows;
    }

    const {
        statusField = "status",
        dateField = "created_at",
        textFields = [],
    } = options;

    const params = parseQueryFilter(queryFilter);
    const status = params.get("status");
    const startDate = params.get("start_date");
    const endDate = params.get("end_date");
    const reservedKeys = new Set(["status", "start_date", "end_date"]);

    const activeTextFilters = Array.from(params.entries())
        .filter(([key, value]) => {
            if (!value || reservedKeys.has(key)) {
                return false;
            }
            if (textFields.length === 0) {
                return true;
            }
            return textFields.includes(key);
        })
        .map(([key, value]) => ({ key, value }));

    return rows.filter((row) => {
        if (status !== null && status !== "") {
            const rowStatus = getNestedValue(row, statusField);
            if (rowStatus !== undefined && rowStatus !== null) {
                if (String(rowStatus) !== String(status)) {
                    return false;
                }
            }
        }

        const matchesAllTextFilters = activeTextFilters.every(
            ({ key, value }) => matchesTextFilter(row, key, value)
        );
        if (!matchesAllTextFilters) {
            return false;
        }

        if (!matchesDateFilter(row, dateField, startDate, endDate)) {
            return false;
        }

        return true;
    });
}
